import React from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'

import { put } from '../../utils/service'
import Alert from '../alert/alert'

interface ChangePasswordProps {
  userId: number
  onClose: () => void
}

const ChangePassword = (props: ChangePasswordProps): JSX.Element => {
  const { t } = useTranslation()

  const [oldPassword, setOldPassword] = React.useState<string>('')
  const [newPassword, setNewPassword] = React.useState<string>('')
  const [confirmNewPassword, setConfirmNewPassword] = React.useState<string>('')
  const [updatePasswordError, setUpdatePasswordError] = React.useState<string>('')
  const [isUpdatingPassword, setIsUpdatingPassword] = React.useState<boolean>(false)
  const [hasUpdatePasswordSuccess, setHasUpdatePasswordSuccess] = React.useState<boolean>(false)

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    // errors: required field, passwords do not match, old password do not match (403 from server), server error
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      setUpdatePasswordError(t('settingsview.requiredfieldsmissing'))
    } else if (newPassword !== confirmNewPassword) {
      setUpdatePasswordError(t('settingsview.passwordsdoesnotmatch'))
    } else {
      // client side validation ok, PUT to server
      setIsUpdatingPassword(true)
      setUpdatePasswordError('')
      put<{ old_password: string; new_password: string }, {}>(`/users/${props.userId}/password/`, {
        old_password: oldPassword,
        new_password: newPassword,
      })
        .then(() => {
          setIsUpdatingPassword(false)
          setHasUpdatePasswordSuccess(true)
          setOldPassword('')
          setNewPassword('')
          setConfirmNewPassword('')
        })
        .catch((error: AxiosError) => {
          setIsUpdatingPassword(false)
          if (error.response?.status === 403) {
            setUpdatePasswordError(t('settingsview.oldpasswordincorrect'))
          } else if (error.response?.status === 422 && error.response?.data) {
            setUpdatePasswordError(t('settingsview.passwordchangeerror') + ': ' + error.response.data)
          } else {
            setUpdatePasswordError(t('settingsview.passwordchangeerror'))
          }
        })
    }
  }

  return (
    <form onSubmit={onFormSubmit}>
      <Stack alignItems="flex-start" spacing={2}>
        {updatePasswordError && <Alert severity="error">{updatePasswordError}</Alert>}
        {hasUpdatePasswordSuccess && <Alert severity="success">{t('settingsview.passwordchanged')}</Alert>}
        <Typography component="h1" variant="h4">
          {t('settingsview.changepassword')}
        </Typography>
        <TextField
          variant="outlined"
          type="password"
          label={t('settingsview.oldpassword')}
          value={oldPassword}
          required={true}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setOldPassword(event.target.value)}
        />
        <TextField
          variant="outlined"
          type="password"
          label={t('settingsview.newpassword')}
          value={newPassword}
          required={true}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNewPassword(event.target.value)}
        />
        <TextField
          variant="outlined"
          type="password"
          label={t('settingsview.confirmnewpassword')}
          value={confirmNewPassword}
          required={true}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setConfirmNewPassword(event.target.value)}
        />
        <Stack spacing={2} direction="row">
          <LoadingButton type="submit" variant="outlined" color="primary" loading={isUpdatingPassword}>
            {t('settingsview.updatepassword')}
          </LoadingButton>
          <Button onClick={props.onClose} disabled={isUpdatingPassword}>
            {t('settingsview.goback')}
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}

export default ChangePassword
